import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useOfflineDashboard } from 'src/contexts/OfflineDashboardContext';
import { Box, Button, Container, Grid, Paper, Tab, Tabs, Typography } from '@material-ui/core';
import { Send } from '@material-ui/icons';
import { Customer } from './Customer';
import { Details } from './Details';
import { Product } from './Product';
import { Defect } from './Defect';
import { Actions } from './Actions';
import { Attachments } from './Attachments';
import Compress from 'compress.js';
import { base64ToBlob, blobToFile } from '../utils/file';
import { useEffectOnce } from 'src/hooks/useEffectOnce';
import { getToken } from 'src/utils/Common';

// Basit form yönetimi hook'u
function useSimpleForm({ defaultValues, onSubmit }) {
  const originalValuesRef = React.useRef(defaultValues || {});
  const [values, setValues] = React.useState(defaultValues || {});
  const [isDirty, setIsDirty] = React.useState(false);
  const listenersRef = React.useRef(new Set());

  const notify = React.useCallback(() => {
    listenersRef.current.forEach(l => l());
  }, []);

  const computeDirty = React.useCallback(
    (next) => JSON.stringify(next) !== JSON.stringify(originalValuesRef.current),
    []
  );

  const setFieldValue = React.useCallback((field, value) => {
    setValues(prev => {
      const next = { ...prev, [field]: typeof value === 'function' ? value(prev[field]) : value };
      setIsDirty(computeDirty(next));
      return next;
    });
  }, [computeDirty]);

  const getFieldValue = React.useCallback((field) => {
    return values[field];
  }, [values]);

  const reset = React.useCallback((nextValues, { keepDefaultValues } = {}) => {
    const finalVals = nextValues || originalValuesRef.current;
    originalValuesRef.current = keepDefaultValues ? originalValuesRef.current : finalVals;
    setValues(finalVals);
    setIsDirty(false);
    notify();
  }, [notify]);


  
  const handleSubmit = React.useCallback((e) => { 

    if (e?.preventDefault) e.preventDefault();
    onSubmit && onSubmit({ value: values, reset });
  }, [values, onSubmit, reset]);

  const subscribe = React.useCallback((listener) => {
    listenersRef.current.add(listener);
    return () => listenersRef.current.delete(listener);
  }, []);

  React.useEffect(() => {
    notify(); // ilk mount'ta
  }, [notify]);

  // values değiştikçe dinleyicilere haber ver
  React.useEffect(() => {
    notify();
  }, [values, notify]);

  return {
    values,
    setFieldValue,
    getFieldValue,
    reset,
    handleSubmit,
    isDirty,
    subscribe
  };
}

export function WorkOrder() {
    const [activeTab, setActiveTab] = useState(0);
    const history = useHistory();

    const params = useParams();
    const workOrderIdParam = parseInt(params.workOrderId, 10);
    const workOrderSubmissionKey = `work-order-${workOrderIdParam}`;
    const workOrderNewFilesSubmissionKey = `work-order-new-files-${workOrderIdParam}`;
    const workOrderDeleteFilesSubmissionKey = `work-order-delete-files-${workOrderIdParam}`;

    const {
        user,
        data,
        dataLoading,
        submissions,
        submissionsLoading,
        cacheSubmission,
        deleteSubmission,
        setOnlineRedirectBlocked
    } = useOfflineDashboard({
        dataKeys: ['WorkOrders'],
        submissionKeys: [workOrderSubmissionKey, workOrderNewFilesSubmissionKey, workOrderDeleteFilesSubmissionKey]
    });

    const initialWorkOrder = useMemo(() => {
        const currentWorkOrder = data?.WorkOrders?.find(wo => wo.WorkOrderId === workOrderIdParam) || {};
        const woSubmission = submissions?.[workOrderSubmissionKey]?.payload?.[0];
        const woNewFilesSubmission = submissions?.[workOrderNewFilesSubmissionKey];
        const woDeleteFilesSubmission = submissions?.[workOrderDeleteFilesSubmissionKey];

        if (!currentWorkOrder || Object.keys(currentWorkOrder).length === 0) {
            return {};
        }

        currentWorkOrder.Labours = currentWorkOrder.Labours.map(labour => ({
            ...labour,
            LabourPrice: labour.Price,
            LabourPriceWithWat: labour.PriceWithWat
        }));

        currentWorkOrder.Materials = currentWorkOrder.Materials.map(material => ({
            ...material,
            MaterialPrice: material.Price,
            MaterialPriceWithWat: material.PriceWithWat
        }));

        if (woSubmission) {
            const submissionDefects = woSubmission.Defects.map(sDefect => {
                const defect = currentWorkOrder.Defects.find(d => d.Code === sDefect.DefectId);
                return {
                    ...defect,
                    DefectId: sDefect.DefectId
                };
            });

            const submissionLabours = woSubmission.Labours.map(sLabour => {
                const labour = currentWorkOrder.Labours.find(l => l.LabourId === sLabour.LabourId);
                return {
                    ...labour,
                    DefectId: sLabour.DefectId,
                    LabourPrice: labour.Price,
                    LabourPriceWithWat: labour.PriceWithWat
                };
            });

            const submissionMaterials = woSubmission.Materials.map(sMaterial => {
                const material = currentWorkOrder.Materials.find(m => m.MaterialId === sMaterial.MaterialId);
                return {
                    ...material,
                    DefectId: sMaterial.DefectId,
                    Count: sMaterial.Count,
                    MaterialPrice: material.Price,
                    MaterialPriceWithWat: material.PriceWithWat
                };
            });

            currentWorkOrder.RepairCompletedDate =
                woSubmission.RepairCompletedDate || currentWorkOrder.RepairCompletedDate;

            currentWorkOrder.WorkOrderDetails = submissionDefects;
            currentWorkOrder.WorkOrderLabours = submissionLabours;
            currentWorkOrder.WorkOrderMaterials = submissionMaterials;
        }

        if (woNewFilesSubmission?.length > 0) {
            const newFiles = woNewFilesSubmission.map(({ payload }) => ({
                id: payload.id,
                FileName: payload.Title,
                Type: Number(payload.FileType),
                FileType: payload.MIMEType,
                FileData: payload.OriginalData,
                WorkOrderId: Number(payload.WorkOrderId),
                CompressedData: payload.CompressedData
            }));
            newFiles.forEach(file => {
                const fileAlreadyExists = currentWorkOrder.Files.find(f => f.id === file.id);
                if (!fileAlreadyExists) {
                    currentWorkOrder.Files = [...currentWorkOrder.Files, file];
                }
            });
        }

        if (woDeleteFilesSubmission) {
            const deletedFileIds = woDeleteFilesSubmission.map(item => item.Id);
            currentWorkOrder.Files = currentWorkOrder.Files.filter(f => !deletedFileIds.includes(f.Id));
        }

        return currentWorkOrder;
    }, [data, submissions]);

    // form oluşturma (useForm yerine)
    const form = useSimpleForm({
        defaultValues: initialWorkOrder,
        onSubmit: async ({ value, reset }) => {
            cacheSubmission(workOrderSubmissionKey, {
              url: process.env.REACT_APP_API_URL + '/Offline/SetWorkOrderUpdateForOffline',
              method: 'POST',
              payload: [
                {
                  WorkOrderId: value.WorkOrderId,
                  EmployeeId: value.EmployeeId,
                  AppointmentDate: value.AppointmentDate,
                  ContactName: value.ContactName,
                  ContactPhone: value.ContactPhone,
                  ModelCode: value.ModelCode,
                  SerialNumber: value.SerialNumber,
                  WarrantyDate: value.WarrantyDate,
                  WarrantyTypeId: value.WarrantyTypeId,
                  LatestStatus: value.LatestStatus,
                  Notes: value.Notes,
                  IssueId: value.IssueId,
                  PaymentTypeId: value.PaymentTypeId,
                  ProjectCode: value.ProjectCode,
                  TaskTypeId: value.TaskTypeId,
                  RepairCompletedDate: value.RepairCompletedDate,
                  StatusLatitude: '41.0288128',
                  StatusLongitude: '29.16352',
                  Defects: (value.WorkOrderDetails || []).map(defect => ({
                      DefectId: defect.DefectId
                  })),
                  Labours: (value.WorkOrderLabours || []).map(labour => ({
                      LabourId: labour.LabourId,
                      DefectId: labour.DefectId,
                      LabourCode: labour.LabourCode,
                      LabourDesc: labour.LabourDesc,
                      Price: labour.LabourPrice,
                      PriceWithWat: labour.LabourPriceWithWat,
                      VatRate: labour.VatRate,
                      VatId: labour.VatId,
                      LabourPriceId: labour.PriceId
                  })),
                  Materials: (value.WorkOrderMaterials || []).map(material => ({
                      MaterialId: material.MaterialId,
                      DefectId: material.DefectId,
                      MaterialCode: material.MaterialCode,
                      Count: material.Count,
                      MaterialDesc: material.MaterialDesc,
                      Price: material.MaterialPrice,
                      PriceWithWat: material.MaterialPriceWithWat,
                      VatRate: material.VatRate,
                      VatId: material.VatId,
                      MaterialPriceListId: material.PriceListId,
                      MaterialPriceId: material.PriceId
                  }))
                }
              ]
            });

            const newFiles = (value.Files || []).filter(file => !file.Id);
            if (!newFiles.length > 0) {
              deleteSubmission(workOrderNewFilesSubmissionKey);
            } else {
              const newFilesSubmissions = await Promise.all(
                newFiles.map(async file => {
                  if (!file.CompressedData) {
                    const compress = new Compress();
                    const blob = base64ToBlob(file.FileData);
                    const resizedImage = await compress.compress([blobToFile(blob, file.FileName)], {
                      size: 0.3,
                      quality: 1,
                      maxWidth: 1920,
                      maxHeight: 1920,
                      resize: true
                    });
                    file.CompressedData = resizedImage[0].data;
                  }
                  return {
                    url: process.env.REACT_APP_API_URL + '/Technician/FileImportExport/SiriusFileUpload',
                    method: 'POST',
                    payload: {
                      id: file.id,
                      Title: file.FileName,
                      MIMEType: file.FileType,
                      WorkOrderId: String(value.WorkOrderId),
                      FileType: String(file.Type),
                      FileData: file.CompressedData,
                      OriginalData: file.FileData
                    }
                  };
                })
              );
              cacheSubmission(workOrderNewFilesSubmissionKey, newFilesSubmissions);
            }

            const deletedFiles = (initialWorkOrder.Files || []).filter(
              file => file.Id && !(value.Files || []).some(f => f.Id === file.Id)
            );

            if (deletedFiles.length > 0) {
              const deletedFilesSubmissions = deletedFiles.map(file => ({
                url: process.env.REACT_APP_API_URL + `/Technician/DeleteSiriusFile/${file.Id}/${file.WorkOrderId}`,
                Id: file.Id,
                method: 'GET'
              }));
              cacheSubmission(workOrderDeleteFilesSubmissionKey, deletedFilesSubmissions);
            }
            
            form.reset(value, { keepDefaultValues: true });
        }
    });

    // useStore yerine direkt form.values
    const { CustomerFullName, ReceiptNo, IssueReason, Status } = form.values;
    const isDirty = form.isDirty;

    // initialWorkOrder değişiminde dirty değilse güncelle
    React.useEffect(() => {
      if (!form.isDirty && initialWorkOrder && Object.keys(initialWorkOrder).length > 0) {
        form.reset(initialWorkOrder, { keepDefaultValues: false });
      }
    }, [initialWorkOrder]); // eslint-disable-line

    React.useEffect(() => {
      if (isDirty) {
        setOnlineRedirectBlocked(true);
      } else {
        setOnlineRedirectBlocked(false);
      }
    }, [isDirty, setOnlineRedirectBlocked]);

    useEffectOnce(() => {
      return () => {
        setOnlineRedirectBlocked(false);
      }
    });

    if (dataLoading || submissionsLoading || !data || !submissions || !user) return null;

    const tabsData = {
      user,
      form
    };

    const tabs = [
        { label: 'Customer', component: Customer },
        { label: 'Details', component: Details },
        { label: 'History', component: null },
        { label: 'Product', component: Product },
        { label: 'Warranty', component: null },
        { label: 'Library', component: null },
        { label: 'Technical Support', component: null },
        { label: 'Defect', component: Defect },
        { label: 'Exchange', component: null },
        { label: 'Actions', component: Actions },
        { label: 'Attachments', component: Attachments },
        { label: 'Print', component: null },
        { label: 'Comment', component: null },
        { label: 'Digiport', component: null }
    ];

    return (
      <form onSubmit={form.handleSubmit}>
            <Box py={3}>
                <Grid container direction="column" spacing={1}>
                    <Grid item>
                        <Grid container justifyContent="space-between">
                            <Grid item>
                                <Typography
                                    variant="h5"
                                    gutterBottom
                                >{`${CustomerFullName}, ${ReceiptNo}, ${Status}`}</Typography>
                                <Typography variant="body2" gutterBottom>
                                    {IssueReason}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Button
                                    color="primary"
                                    variant="contained"
                                    onClick={() => history.push(`/offline/work-orders`)}
                                >
                                    Back
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Box maxWidth="100%" mb={2}>
                            <Tabs
                                value={activeTab}
                                onChange={(_, newValue) => setActiveTab(newValue)}
                                textColor="secondary"
                                scrollButtons="on"
                                indicatorColor="primary"
                                variant="scrollable"
                            >
                                {tabs.map((tab, index) => (
                                    <Tab key={index} label={tab.label} value={index} disabled={!tab.component} />
                                ))}
                            </Tabs>
                        </Box>
                        {tabs.map((tab, index) => (
                            <Box key={index} hidden={activeTab !== index}>
                                {tab.component ? (
                                    <tab.component {...tabsData} />
                                ) : (
                                    <Typography variant="body1" align="center">
                                        {`${tab.label} content is not available.`}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Grid>
                </Grid>
            </Box>
            <Paper
                style={{ position: 'fixed', bottom: 0, left: 0, width: '100vw', zIndex: 1000, padding: 5 }}
                elevation={2}
            >
                <Container maxWidth="md">
                    <Button
                        fullWidth
                        color="primary"
                        variant="contained"
                        onClick={form.handleSubmit}
                        disabled={!isDirty}
                        endIcon={<Send />}
                    >
                        Send To Center
                    </Button>
                </Container>
            </Paper>
        </form>
    );
}
